import type { IncomeBoat, IncomeOwnership, UpsertBoatIncomeSettingsInput } from '@sea-snag/shared';
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
  Input,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  Table,
  TableBody,
  TableCell,
  TableFooter,
  TableHead,
  TableHeader,
  TableRow,
} from '@sea-snag/ui';
import { ArrowDown, ArrowUp, Pencil } from 'lucide-react';
import { useMemo, useState } from 'react';

import { boatRow, type BoatRowTotals, formatCents, type IncomeTotals } from '../../lib/income';

type SortKey = 'name' | 'bookings' | 'gross' | 'tax' | 'net' | 'share' | 'owner' | 'company';

interface Row {
  boat: IncomeBoat;
  totals: BoatRowTotals;
}

const sortValue = (r: Row, key: SortKey): number | string | null => {
  switch (key) {
    case 'name':
      return r.boat.name.toLowerCase();
    case 'bookings':
      return r.totals.bookings;
    case 'gross':
      return r.totals.grossCents;
    case 'tax':
      return r.totals.taxCents;
    case 'net':
      return r.totals.netCents;
    case 'share':
      return r.boat.ownership === 'external' ? r.boat.sharePct : null;
    case 'owner':
      return r.totals.ownerCents;
    case 'company':
      return r.totals.companyCents;
  }
};

/**
 * Each boat's season (#217): bookings, gross, tax, net, the owner's share and
 * the two amounts, with a totals row matching the cards. The ownership and the
 * owner share are edited here, for this season only, and stay locked until
 * Edit is pressed.
 */
export function PerBoatIncomeTable({
  boats,
  currency,
  year,
  totals,
  canEdit,
  isPending,
  onSaveSettings,
}: {
  boats: IncomeBoat[];
  currency: string;
  year: number;
  totals: IncomeTotals;
  canEdit: boolean;
  isPending: boolean;
  onSaveSettings: (boatPid: string, data: UpsertBoatIncomeSettingsInput) => Promise<unknown>;
}) {
  const [sort, setSort] = useState<{ key: SortKey; desc: boolean }>({ key: 'name', desc: false });
  const rows = useMemo(() => {
    const list = boats.map((boat) => ({ boat, totals: boatRow(boat, currency) }));
    return list.sort((a, b) => {
      const x = sortValue(a, sort.key);
      const y = sortValue(b, sort.key);
      // Unknown values always last, whichever way the column is sorted.
      if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
      const order = x < y ? -1 : x > y ? 1 : 0;
      return sort.desc ? -order : order;
    });
  }, [boats, currency, sort]);

  const header = (key: SortKey, label: string, right = true) => (
    <TableHead className={right ? 'text-right' : undefined}>
      <button
        type="button"
        className="inline-flex items-center gap-1 hover:text-foreground"
        onClick={() => setSort((s) => ({ key, desc: s.key === key ? !s.desc : key !== 'name' }))}
      >
        {label}
        {sort.key === key &&
          (sort.desc ? (
            <ArrowDown className="size-3" aria-hidden />
          ) : (
            <ArrowUp className="size-3" aria-hidden />
          ))}
      </button>
    </TableHead>
  );
  const money = (cents: number | null) => (cents === null ? '—' : formatCents(cents, currency));
  const partial = totals.unresolvedBoats > 0;

  return (
    <Card>
      <CardHeader className="pb-2">
        <CardTitle className="text-base">Per boat</CardTitle>
      </CardHeader>
      <CardContent className="overflow-x-auto">
        <Table>
          <TableHeader className="sticky top-0 bg-background">
            <TableRow>
              {header('name', 'Boat', false)}
              {header('bookings', 'Bookings')}
              {header('gross', 'Gross')}
              {header('tax', 'Tax')}
              {header('net', 'Net')}
              {header('share', 'Owner share')}
              {header('owner', 'Owner')}
              {header('company', 'Company')}
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map(({ boat, totals: t }) => (
              <TableRow key={boat.boatPid}>
                <TableCell>
                  <div className="flex flex-wrap items-center gap-1 font-medium">
                    {boat.name}
                    {boat.ownership === 'company' && (
                      <Badge variant="secondary" className="px-1 py-0 text-[10px]">
                        Company-owned
                      </Badge>
                    )}
                    {!boat.inFleet && (
                      <Badge variant="outline" className="px-1 py-0 text-[10px]">
                        Left the fleet
                      </Badge>
                    )}
                  </div>
                  {boat.model && <div className="text-xs text-muted-foreground">{boat.model}</div>}
                </TableCell>
                <TableCell className="text-right tabular-nums">{t.bookings}</TableCell>
                <TableCell className="text-right tabular-nums">{money(t.grossCents)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(t.taxCents)}</TableCell>
                <TableCell className="text-right tabular-nums">{money(t.netCents)}</TableCell>
                <TableCell className="text-right">
                  <OwnershipCell
                    boat={boat}
                    year={year}
                    canEdit={canEdit}
                    isPending={isPending}
                    onSave={(data) => onSaveSettings(boat.boatPid, data)}
                  />
                </TableCell>
                <TableCell className="text-right tabular-nums">
                  {/* A company-owned boat has no owner: "—", not €0 (brief §5). */}
                  {boat.ownership === 'company' ? '—' : money(t.ownerCents)}
                </TableCell>
                <TableCell className="text-right tabular-nums">{money(t.companyCents)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
          <TableFooter>
            <TableRow>
              <TableCell className="font-medium">Total</TableCell>
              <TableCell className="text-right tabular-nums">{totals.bookings}</TableCell>
              <TableCell className="text-right tabular-nums">{money(totals.grossCents)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(totals.taxCents)}</TableCell>
              <TableCell className="text-right tabular-nums">{money(totals.netCents)}</TableCell>
              <TableCell />
              <TableCell className="text-right tabular-nums">
                {money(totals.ownerCents)}
                {partial && (
                  <div className="text-xs font-normal text-muted-foreground">Partial</div>
                )}
              </TableCell>
              <TableCell className="text-right tabular-nums">
                {money(totals.companyCents)}
                {partial && (
                  <div className="text-xs font-normal text-muted-foreground">Partial</div>
                )}
              </TableCell>
            </TableRow>
          </TableFooter>
        </Table>
      </CardContent>
    </Card>
  );
}

/**
 * A boat's ownership and owner share for the season, read-only until Edit.
 * Company-owned: "Not applicable". External without a share: "Set owner
 * share", which is not 0%. Unresolved: "Set ownership type".
 */
function OwnershipCell({
  boat,
  year,
  canEdit,
  isPending,
  onSave,
}: {
  boat: IncomeBoat;
  year: number;
  canEdit: boolean;
  isPending: boolean;
  onSave: (data: UpsertBoatIncomeSettingsInput) => Promise<unknown>;
}) {
  const [editing, setEditing] = useState(false);
  const [ownership, setOwnership] = useState<IncomeOwnership>(boat.ownership ?? 'external');
  const [share, setShare] = useState(boat.sharePct === null ? '' : String(boat.sharePct));

  const open = () => {
    setOwnership(boat.ownership ?? 'external');
    setShare(boat.sharePct === null ? '' : String(boat.sharePct));
    setEditing(true);
  };

  if (!editing) {
    const shown =
      boat.ownership === 'company'
        ? 'Not applicable'
        : boat.ownership === 'external' && boat.sharePct !== null
          ? `${boat.sharePct}%`
          : null;
    const missing = boat.ownership === null ? 'Set ownership type' : 'Set owner share';
    return (
      <div className="flex flex-col items-end">
        <div className="flex items-center justify-end gap-1">
          {shown !== null ? (
            <span className="tabular-nums">{shown}</span>
          ) : canEdit ? (
            <Button variant="link" size="sm" className="h-auto p-0 text-xs" onClick={open}>
              {missing}
            </Button>
          ) : (
            <span className="text-xs text-muted-foreground">{missing}</span>
          )}
          {shown !== null && canEdit && (
            <Button
              variant="ghost"
              size="sm"
              className="h-6 w-6 p-0"
              aria-label={`Edit ${boat.name}'s ownership and owner share for ${year}`}
              onClick={open}
            >
              <Pencil className="size-3" aria-hidden />
            </Button>
          )}
        </div>
        {boat.carriedFromYear !== null && (
          <span className="text-[10px] text-muted-foreground">from {boat.carriedFromYear}</span>
        )}
      </div>
    );
  }

  const value = Number(share);
  const shareValid = share.trim() === '' || (value >= 0 && value <= 100);
  return (
    <div className="flex flex-col items-end gap-1">
      <span className="text-[10px] text-muted-foreground">Owner share for {year}</span>
      <Select
        value={ownership}
        onValueChange={(v) => setOwnership(v === 'company' ? 'company' : 'external')}
      >
        <SelectTrigger className="h-7 w-40 text-xs" aria-label="Ownership type">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="external">External owner</SelectItem>
          <SelectItem value="company">Company-owned</SelectItem>
        </SelectContent>
      </Select>
      {ownership === 'external' && (
        <div className="flex items-center gap-1">
          <Input
            type="number"
            min={0}
            max={100}
            step="0.01"
            aria-label="Owner share %"
            placeholder="Not agreed"
            className="h-7 w-20 text-right text-xs"
            value={share}
            onChange={(e) => setShare(e.target.value)}
          />
          <span className="text-xs text-muted-foreground">%</span>
        </div>
      )}
      <div className="flex gap-1">
        <Button
          size="sm"
          className="h-7 px-2 text-xs"
          disabled={!shareValid || isPending}
          onClick={() =>
            void onSave({
              year,
              ownership,
              sharePct: ownership === 'company' || share.trim() === '' ? null : value,
            }).then(() => setEditing(false))
          }
        >
          Save
        </Button>
        <Button
          variant="ghost"
          size="sm"
          className="h-7 px-2 text-xs"
          onClick={() => setEditing(false)}
        >
          Cancel
        </Button>
      </div>
    </div>
  );
}
